import type { FormaPagamento } from "@/hooks/useFormasPagamento";

/** Normaliza valor legado (tipo/nome/id) para o id da forma de pagamento. */
export function resolveFormaPagamentoId(
  stored: string | null | undefined,
  formas: FormaPagamento[]
): string {
  if (!stored?.trim()) return "";
  const s = stored.trim();
  const byId = formas.find((f) => f.id.toString() === s);
  if (byId) return byId.id.toString();
  const byTipo = formas.find((f) => f.tipo === s);
  if (byTipo) return byTipo.id.toString();
  const byNome = formas.find((f) => f.nome.toLowerCase() === s.toLowerCase());
  if (byNome) return byNome.id.toString();
  return "";
}

export function isFormaPagamentoCartao(
  formaPagamentoId: string,
  formas: FormaPagamento[]
): boolean {
  if (!formaPagamentoId) return false;
  const forma = formas.find((f) => f.id.toString() === formaPagamentoId);
  return forma?.tipo === "cartao";
}

export function getFormaPagamentoByIdOrNome(
  valor: string | null | undefined,
  formas: FormaPagamento[]
): FormaPagamento | undefined {
  if (!valor?.trim()) return undefined;
  const id = resolveFormaPagamentoId(valor, formas);
  if (id) return formas.find((f) => f.id.toString() === id);
  const s = valor.trim().toLowerCase();
  return formas.find((f) => f.nome.toLowerCase() === s);
}

/**
 * Quantidade de parcelas a gravar no orçamento ao selecionar a forma.
 * - Cartão: usa a escolha do usuário (1..parcelas_max).
 * - Demais: usa parcelas_max do cadastro (ex.: Boleto 3X → 3).
 */
export function numeroParcelasAoSelecionarForma(
  formaPagamentoId: string,
  formas: FormaPagamento[],
  parcelasCartaoEscolhidas?: number | null
): number {
  const forma = getFormaPagamentoByIdOrNome(formaPagamentoId, formas);
  const max = Math.max(1, Number(forma?.parcelas_max) || 1);
  if (isFormaPagamentoCartao(formaPagamentoId, formas)) {
    const escolhido = Number(parcelasCartaoEscolhidas);
    if (Number.isFinite(escolhido) && escolhido >= 1) {
      return Math.min(escolhido, max);
    }
    return max;
  }
  return max;
}

/** Extrai Nx do nome (ex.: "Boleto Parcelado em 3X" → 3). */
export function extrairParcelasDoNome(nome?: string | null): number | null {
  if (!nome?.trim()) return null;
  const m = nome.match(/(\d+)\s*[xX]\b/);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return Number.isFinite(n) && n >= 1 && n <= 48 ? n : null;
}

/**
 * Resolve quantas parcelas gerar no contrato/financeiro.
 * Prioriza parcelas_max do cadastro quando a forma não é cartão
 * (corrige orçamentos antigos com parcelas_cartao=1 em boleto 3x).
 */
export function resolverNumeroParcelasGeracao(opts: {
  numeroParcelasOrcamento?: number | null;
  parcelasCartaoOrcamento?: number | null;
  parcelasMaxForma?: number | null;
  isCartao?: boolean;
  formaNome?: string | null;
}): number {
  const maxCadastro = Number(opts.parcelasMaxForma);
  const max =
    Number.isFinite(maxCadastro) && maxCadastro > 0
      ? maxCadastro
      : extrairParcelasDoNome(opts.formaNome) ?? 0;
  const informadoRaw =
    opts.numeroParcelasOrcamento ?? opts.parcelasCartaoOrcamento ?? null;
  const informado =
    informadoRaw != null && Number(informadoRaw) > 0
      ? Number(informadoRaw)
      : null;

  const isCartao =
    opts.isCartao === true ||
    (opts.formaNome || "").toLowerCase().includes("cartão") ||
    (opts.formaNome || "").toLowerCase().includes("cartao");

  if (isCartao) {
    if (informado != null) {
      return max > 0 ? Math.min(Math.max(1, informado), max) : Math.max(1, informado);
    }
    return max > 0 ? max : 1;
  }

  // Boleto parcelado / PIX / à vista: sempre o cadastro (parcelas_max)
  if (max > 0) return max;

  if (informado != null) return Math.max(1, informado);
  return 1;
}
